// FullCustomerHistory — bottom of CustomerPanel.
//
// Polish #16 — "related threads widget." This section used to hide every
// past conversation behind a single "N past conversations" toggle, so
// Kimi had to click before she knew whether this customer had written
// in before. That buried the most useful context.
//
// Behavior now:
//   - Always render the 3 most recent past threads (no click needed).
//   - If there are more, show a "Show N more" toggle beneath them.
//   - Count line in the header remains so Kimi can still see the total
//     at a glance.
//
// Data: useCustomerHistory returns conversations sorted newest-first by
// last_message_at on the backend, so the slice is meaningful.
//
// Navigation: clicking a row routes to /thread/:id without a bucket
// context (there isn't one — this is a manual jump, not a bucket step).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerHistory } from '@/lib/hooks/useCustomerHistory';
import { Tag } from '@/components/brand/Tag';
import type { TagVariant } from '@/components/brand/Tag';

interface FullCustomerHistoryProps {
  customerEmail: string | null;
  currentConversationId: string;
}

/** How many recent threads render inline before the "Show N more" toggle. */
const INLINE_LIMIT = 3;

const CHANNEL_TAG: Record<string, TagVariant> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
};
const CHANNEL_LABEL: Record<string, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Brokerage',
};

function relative(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function FullCustomerHistory({
  customerEmail,
  currentConversationId,
}: FullCustomerHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useCustomerHistory(customerEmail, currentConversationId);

  if (!customerEmail) return null;
  const prior = data?.conversations ?? [];
  if (!isLoading && prior.length === 0) return null;

  const inline = prior.slice(0, INLINE_LIMIT);
  const overflow = prior.slice(INLINE_LIMIT);
  const total = prior.length;

  function go(id: string) {
    navigate(`/thread/${id}`, { state: undefined });
  }

  return (
    <div className="cust-full-history">
      <div className="cust-related-head">
        <span className="eyebrow">Related threads</span>
        <span className="cust-related-count">
          {isLoading
            ? '...'
            : `${total} total`}
        </span>
      </div>

      {isLoading ? (
        <div className="cust-related-loading">Looking for prior threads...</div>
      ) : (
        <>
          <div className="cust-history-list">
            {inline.map((c) => (
              <button
                key={c.id}
                type="button"
                className="cust-history-row"
                onClick={() => go(c.id)}
              >
                <div className="row-head">
                  <div className="row-tags">
                    <Tag variant={c.brand === 'md' ? 'brand-md' : 'brand-hk'}>
                      {c.brand === 'md' ? 'MD' : 'HK'}
                    </Tag>
                    <Tag variant={CHANNEL_TAG[c.channel] ?? 'neutral'}>
                      {CHANNEL_LABEL[c.channel] ?? c.channel}
                    </Tag>
                  </div>
                  <span className="row-when">{relative(c.last_message_at)}</span>
                </div>
                <div className="row-subject">
                  {c.ai_summary ?? c.subject ?? '(no subject)'}
                </div>
              </button>
            ))}
          </div>

          {overflow.length > 0 ? (
            <>
              <button
                type="button"
                className="cust-history-toggle"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                <span>
                  {expanded
                    ? `Hide ${overflow.length} older`
                    : `Show ${overflow.length} more`}
                </span>
                <span aria-hidden>{expanded ? '\u25be' : '\u25b8'}</span>
              </button>
              {expanded ? (
                <div className="cust-history-list">
                  {overflow.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="cust-history-row"
                      onClick={() => go(c.id)}
                    >
                      <div className="row-head">
                        <div className="row-tags">
                          <Tag variant={c.brand === 'md' ? 'brand-md' : 'brand-hk'}>
                            {c.brand === 'md' ? 'MD' : 'HK'}
                          </Tag>
                          <Tag variant={CHANNEL_TAG[c.channel] ?? 'neutral'}>
                            {CHANNEL_LABEL[c.channel] ?? c.channel}
                          </Tag>
                        </div>
                        <span className="row-when">{relative(c.last_message_at)}</span>
                      </div>
                      <div className="row-subject">
                        {c.ai_summary ?? c.subject ?? '(no subject)'}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
